import { useCallback, useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../shell/Can.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  listMenus,
  createMenu,
  updateMenu,
  deactivateMenu,
  activateMenu,
  listMenuRoles,
  assignMenuRole,
  revokeMenuRole,
  MENU_STATUS_LABELS,
  type MenuItem,
  type RoleMenuMap,
} from '../../api/menusApi.js';
import { listRoles, type Role } from '../../api/rolesApi.js';
import { Button } from '../../../../shared/components/Button.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

const MENU_CODE_RE = /^[A-Z][A-Z0-9_]{1,63}$/;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MenuMasterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly MenuItem[] | null>(null);
  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      const [menus, roleList] = await Promise.all([
        listMenus(includeInactive, token),
        // The menus still list without roles, but the role map would offer
        // nothing to assign — so the failure is said, not shown as "no roles".
        listRoles(token).catch((e: unknown) => {
          setError((e as Error).message);
          return [] as readonly Role[];
        }),
      ]);
      setRows(menus);
      setRoles(roleList);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [includeInactive, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.menuCode.toLowerCase().includes(q) ||
        r.menuName.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const runAction = useCallback(
    async (fn: () => Promise<unknown>, successMsg: string) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await fn();
        setNotice(successMsg);
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  return (
    <main aria-label="menu-master" data-testid="menu-master-page">
      <h1>{t('admin.menu.title')}</h1>
      <p>{t('admin.menu.intro')}</p>

      <div role="toolbar" aria-label="menu-master-toolbar">
        <input
          aria-label="filter"
          placeholder={t('admin.menu.filterPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            aria-label="include-inactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          {t('admin.menu.showInactive')}
        </label>
      </div>

      <Can path="/admin/menus" action="create">
        <CreateMenuForm
          token={token}
          onCreated={(msg) => { setNotice(msg); setError(null); void load(); }}
        />
      </Can>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {!rows ? (
        error ? null : <p>{t('admin.menu.loading')}</p>
      ) : (
        <table aria-label="menu-table">
          <thead>
            <tr>
              <th>{t('admin.menu.col.code')}</th>
              <th>{t('admin.menu.col.name')}</th>
              <th>{t('admin.menu.col.parent')}</th>
              <th>{t('admin.menu.col.seq')}</th>
              <th>{t('admin.menu.col.web')}</th>
              <th>{t('admin.menu.col.mobile')}</th>
              <th>{t('admin.menu.col.icon')}</th>
              <th>{t('admin.menu.col.url')}</th>
              <th>{t('admin.menu.col.status')}</th>
              <th>{t('admin.menu.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered!.length === 0 ? (
              <tr><td colSpan={10}>{t('admin.menu.empty')}</td></tr>
            ) : (
              filtered!.map((m) => (
                <MenuRow
                  key={m.menuId ?? m.menuCode}
                  menu={m}
                  roles={roles}
                  token={token}
                  busy={busy}
                  runAction={runAction}
                />
              ))
            )}
          </tbody>
        </table>
      )}
    </main>
  );
}

// ── Create form (all §10 fields) ────────────────────────────────────────────
function CreateMenuForm({
  token,
  onCreated,
}: {
  token: string | null | undefined;
  onCreated: (msg: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuCode, setMenuCode] = useState('');
  const [menuName, setMenuName] = useState('');
  const [parentMenuCode, setParentMenuCode] = useState('');
  const [menuSequence, setMenuSequence] = useState('100');
  const [webVisible, setWebVisible] = useState(true);
  const [mobileVisible, setMobileVisible] = useState(false);
  const [icon, setIcon] = useState('');
  const [url, setUrl] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setMenuCode(''); setMenuName(''); setParentMenuCode(''); setMenuSequence('100');
    setWebVisible(true); setMobileVisible(false); setIcon(''); setUrl('');
    setEffectiveFromDate(today());
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const code = menuCode.trim().toUpperCase();
    if (!MENU_CODE_RE.test(code)) {
      setFormError(t('admin.menu.err.code'));
      return;
    }
    if (!menuName.trim()) {
      setFormError(t('admin.menu.err.name'));
      return;
    }
    setSubmitting(true);
    try {
      await createMenu(
        {
          menuCode: code,
          menuName: menuName.trim(),
          parentMenuCode: parentMenuCode.trim().toUpperCase() || undefined,
          menuSequence: Number(menuSequence) || 100,
          webVisible,
          mobileVisible,
          icon: icon.trim() || undefined,
          url: url.trim() || undefined,
          effectiveFromDate,
        },
        token,
      );
      reset();
      setOpen(false);
      onCreated(t('admin.menu.created'));
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Button aria-label="new-menu" onClick={() => setOpen((o) => !o)}>
        {open ? t('admin.menu.cancel') : t('admin.menu.new')}
      </Button>
      {open ? (
        <form aria-label="create-menu-form" onSubmit={onSubmit} noValidate onSubmitCapture={validateDateSubmission}>
          <label>{t('admin.menu.f.code')} <input aria-label="menu-code" value={menuCode} onChange={(e) => setMenuCode(e.target.value)} /></label>
          <label>{t('admin.menu.f.name')} <input aria-label="menu-name" value={menuName} onChange={(e) => setMenuName(e.target.value)} /></label>
          <label>{t('admin.menu.f.parentCode')} <input aria-label="parent-code" value={parentMenuCode} onChange={(e) => setParentMenuCode(e.target.value)} /></label>
          <label>{t('admin.menu.f.sequence')} <input aria-label="sequence" type="number" value={menuSequence} onChange={(e) => setMenuSequence(e.target.value)} /></label>
          <label>{t('admin.menu.f.icon')} <input aria-label="icon" value={icon} onChange={(e) => setIcon(e.target.value)} /></label>
          <label>{t('admin.menu.f.url')} <input aria-label="url" value={url} onChange={(e) => setUrl(e.target.value)} /></label>
          <label><input type="checkbox" aria-label="web-visible" checked={webVisible} onChange={(e) => setWebVisible(e.target.checked)} /> {t('admin.menu.f.webVisible')}</label>
          <label><input type="checkbox" aria-label="mobile-visible" checked={mobileVisible} onChange={(e) => setMobileVisible(e.target.checked)} /> {t('admin.menu.f.mobileVisible')}</label>
          <label>{t('admin.menu.f.effectiveFrom')} <DateInput aria-label="effective-from" type="date" value={effectiveFromDate} onValueChange={(nextValue) => setEffectiveFromDate(nextValue)} /></label>
          {formError ? <p role="alert">{formError}</p> : null}
          <Button type="submit" aria-label="submit-create" disabled={submitting}>
            {submitting ? t('admin.menu.saving') : t('admin.menu.create')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

// ── Single menu row with inline edit + status + role mapping ─────────────────
function MenuRow({
  menu,
  roles,
  token,
  busy,
  runAction,
}: {
  menu: MenuItem;
  roles: readonly Role[];
  token: string | null | undefined;
  busy: boolean;
  runAction: (fn: () => Promise<unknown>, msg: string) => Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);
  const isActive = menu.recordStatusCode !== 'INACTIVE';

  return (
    <>
      <tr data-testid={`menu-row-${menu.menuCode}`}>
        <td>{menu.menuCode}</td>
        <td>{menu.menuName}</td>
        <td>{menu.parentMenuCode ?? '—'}</td>
        <td>{menu.menuSequence}</td>
        <td>{menu.webVisible ? t('common.yes') : t('common.no')}</td>
        <td>{menu.mobileVisible ? t('common.yes') : t('common.no')}</td>
        <td>{menu.icon ?? '—'}</td>
        <td>{menu.url ?? '—'}</td>
        <td>{MENU_STATUS_LABELS[menu.recordStatusCode] ?? menu.recordStatusCode ?? '—'}</td>
        <td>
          <Can path="/admin/menus" action="edit">
            <Button aria-label={`edit-${menu.menuCode}`} onClick={() => setEditing((v) => !v)} disabled={busy}>
              {editing ? t('admin.menu.close') : t('admin.menu.edit')}
            </Button>
            {isActive ? (
              <Button
                aria-label={`deactivate-${menu.menuCode}`}
                disabled={busy}
                onClick={() => runAction(() => deactivateMenu(menu.menuId, token), t('admin.menu.deactivated', { code: menu.menuCode }))}
              >
                {t('admin.menu.deactivate')}
              </Button>
            ) : (
              <Button
                aria-label={`activate-${menu.menuCode}`}
                disabled={busy}
                onClick={() => runAction(() => activateMenu(menu.menuId, token), t('admin.menu.activated', { code: menu.menuCode }))}
              >
                {t('admin.menu.activate')}
              </Button>
            )}
          </Can>
          <Button aria-label={`roles-${menu.menuCode}`} onClick={() => setRolesOpen((v) => !v)}>
            {rolesOpen ? t('admin.menu.hideRoleMap') : t('admin.menu.roleMap')}
          </Button>
        </td>
      </tr>
      {editing ? (
        <tr>
          <td colSpan={10}>
            <EditMenuForm menu={menu} token={token} onSaved={(msg) => { setEditing(false); void runAction(async () => undefined, msg); }} />
          </td>
        </tr>
      ) : null}
      {rolesOpen ? (
        <tr>
          <td colSpan={10}>
            <RoleMappingPanel menu={menu} roles={roles} token={token} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ── Inline edit form ─────────────────────────────────────────────────────────
function EditMenuForm({
  menu,
  token,
  onSaved,
}: {
  menu: MenuItem;
  token: string | null | undefined;
  onSaved: (msg: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [menuName, setMenuName] = useState(menu.menuName);
  const [parentMenuCode, setParentMenuCode] = useState(menu.parentMenuCode ?? '');
  const [menuSequence, setMenuSequence] = useState(String(menu.menuSequence));
  const [webVisible, setWebVisible] = useState(menu.webVisible);
  const [mobileVisible, setMobileVisible] = useState(menu.mobileVisible);
  const [icon, setIcon] = useState(menu.icon ?? '');
  const [url, setUrl] = useState(menu.url ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!menuName.trim()) {
      setFormError(t('admin.menu.err.name'));
      return;
    }
    setSubmitting(true);
    try {
      await updateMenu(
        menu.menuId,
        {
          menuName: menuName.trim(),
          parentMenuCode: parentMenuCode.trim().toUpperCase() || undefined,
          menuSequence: Number(menuSequence) || menu.menuSequence,
          webVisible,
          mobileVisible,
          icon: icon.trim() || undefined,
          url: url.trim() || undefined,
          version: menu.version,
        },
        token,
      );
      onSaved(t('admin.menu.updated', { code: menu.menuCode }));
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form aria-label={`edit-menu-form-${menu.menuCode}`} onSubmit={onSubmit} noValidate>
      <label>{t('admin.menu.f.name')} <input aria-label={`edit-name-${menu.menuCode}`} value={menuName} onChange={(e) => setMenuName(e.target.value)} /></label>
      <label>{t('admin.menu.f.parent')} <input aria-label={`edit-parent-${menu.menuCode}`} value={parentMenuCode} onChange={(e) => setParentMenuCode(e.target.value)} /></label>
      <label>{t('admin.menu.f.sequence')} <input aria-label={`edit-sequence-${menu.menuCode}`} type="number" value={menuSequence} onChange={(e) => setMenuSequence(e.target.value)} /></label>
      <label>{t('admin.menu.f.icon')} <input aria-label={`edit-icon-${menu.menuCode}`} value={icon} onChange={(e) => setIcon(e.target.value)} /></label>
      <label>{t('admin.menu.f.url')} <input aria-label={`edit-url-${menu.menuCode}`} value={url} onChange={(e) => setUrl(e.target.value)} /></label>
      <label><input type="checkbox" aria-label={`edit-web-${menu.menuCode}`} checked={webVisible} onChange={(e) => setWebVisible(e.target.checked)} /> {t('admin.menu.f.webVisible')}</label>
      <label><input type="checkbox" aria-label={`edit-mobile-${menu.menuCode}`} checked={mobileVisible} onChange={(e) => setMobileVisible(e.target.checked)} /> {t('admin.menu.f.mobileVisible')}</label>
      {formError ? <p role="alert">{formError}</p> : null}
      <Button type="submit" aria-label={`save-${menu.menuCode}`} disabled={submitting}>
        {submitting ? t('admin.menu.saving') : t('admin.menu.save')}
      </Button>
    </form>
  );
}

// ── Role mapping panel (§10 Role Mapping) ────────────────────────────────────
function RoleMappingPanel({
  menu,
  roles,
  token,
}: {
  menu: MenuItem;
  roles: readonly Role[];
  token: string | null | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const [maps, setMaps] = useState<readonly RoleMenuMap[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roleId, setRoleId] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setMaps(await listMenuRoles(menu.menuId, token));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [menu.menuId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const roleName = (id: string) => roles.find((r) => r.roleId === id)?.roleName ?? id;
  const assignedIds = new Set((maps ?? []).map((m) => m.roleId));
  const assignable = roles.filter((r) => !assignedIds.has(r.roleId));

  const onAssign = async (e: FormEvent) => {
    e.preventDefault();
    if (!roleId) {
      setError(t('admin.menu.err.selectRole'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await assignMenuRole(menu.menuId, roleId, { canView: true, canCreate, canEdit, canDelete }, token);
      setRoleId(''); setCanCreate(false); setCanEdit(false); setCanDelete(false);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (rid: string) => {
    setBusy(true);
    setError(null);
    try {
      await revokeMenuRole(menu.menuId, rid, token);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label={`role-mapping-${menu.menuCode}`}>
      <h3>{t('admin.menu.roleMapping', { name: menu.menuName })}</h3>
      {error ? <p role="alert">{error}</p> : null}
      {!maps ? (
        <p>{t('admin.menu.loading')}</p>
      ) : (
        <table aria-label={`role-map-table-${menu.menuCode}`}>
          <thead>
            <tr><th>{t('admin.menu.rm.role')}</th><th>{t('admin.menu.rm.view')}</th><th>{t('admin.menu.rm.create')}</th><th>{t('admin.menu.rm.edit')}</th><th>{t('admin.menu.rm.delete')}</th><th></th></tr>
          </thead>
          <tbody>
            {maps.length === 0 ? (
              <tr><td colSpan={6}>{t('admin.menu.rm.empty')}</td></tr>
            ) : (
              maps.map((m) => (
                <tr key={m.mapId} data-testid={`role-map-${menu.menuCode}-${m.roleId}`}>
                  <td>{roleName(m.roleId)}</td>
                  <td>{m.canView ? '✓' : ''}</td>
                  <td>{m.canCreate ? '✓' : ''}</td>
                  <td>{m.canEdit ? '✓' : ''}</td>
                  <td>{m.canDelete ? '✓' : ''}</td>
                  <td>
                    <Can path="/admin/menus" action="edit">
                      <Button aria-label={`revoke-${menu.menuCode}-${m.roleId}`} onClick={() => onRevoke(m.roleId)} disabled={busy}>
                        {t('admin.menu.rm.revoke')}
                      </Button>
                    </Can>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
      <Can path="/admin/menus" action="edit">
        <form aria-label={`assign-role-form-${menu.menuCode}`} onSubmit={onAssign}>
          <label htmlFor={`assign-role-${menu.menuCode}`}>{t('admin.menu.rm.role')}</label>
          <select id={`assign-role-${menu.menuCode}`} aria-label={`assign-role-${menu.menuCode}`} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t('admin.menu.rm.selectRole')}</option>
            {assignable.map((r) => (
              <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
            ))}
          </select>
          <label><input type="checkbox" aria-label={`assign-create-${menu.menuCode}`} checked={canCreate} onChange={(e) => setCanCreate(e.target.checked)} /> {t('admin.menu.rm.create')}</label>
          <label><input type="checkbox" aria-label={`assign-edit-${menu.menuCode}`} checked={canEdit} onChange={(e) => setCanEdit(e.target.checked)} /> {t('admin.menu.rm.edit')}</label>
          <label><input type="checkbox" aria-label={`assign-delete-${menu.menuCode}`} checked={canDelete} onChange={(e) => setCanDelete(e.target.checked)} /> {t('admin.menu.rm.delete')}</label>
          <Button type="submit" aria-label={`assign-submit-${menu.menuCode}`} disabled={busy}>{t('admin.menu.rm.mapRole')}</Button>
        </form>
      </Can>
    </section>
  );
}

